import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Stack, Text, Group, Badge, Button, Alert, Skeleton, Table, Loader, Anchor } from '@mantine/core'
import { IconShieldCheck, IconRefresh, IconPlus, IconAlertTriangle } from '@tabler/icons-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import { formatApiError } from '@/api/errors'
import { podeEmAlgumTenant, useAuthStore, useCan, useCurrentTenant } from '@/store/auth'
import { dismissProvisioningRequest, listProvisioningRequests, provisionWaf,
  type ProvisioningRequest } from '@/api/waf'
import { WafProvisionWizard } from './WafProvisionWizard'
import { WafLoteModal } from './WafLoteModal'
import { LotesRecentes } from './LotesRecentes'
import { WafProvisioningModal } from './WafProvisioningModal'
import { WafDeleteModal } from './WafDeleteModal'
import { WafProvisioningHistory } from './WafProvisioningHistory'
import { QuandoPedido } from './QuandoPedido'
import { ProvisioningTimeline } from './ProvisioningTimeline'

export interface ManagedWaf {
  id: number
  name: string
  account_id: string
  region: string
  scope: 'REGIONAL' | 'CLOUDFRONT'
  web_acl_id: string
  web_acl_arn: string
  world: 'own' | 'institutional' | 'tenant'
  tenant_id: number | null
  default_action: string
  sensitive: boolean
  is_managed: boolean
  resource_count?: number
  primary_domain?: string | null
}

const wafApi = {
  list: () =>
    api.get<{ items: ManagedWaf[] }>('/v1/waf/managed').then(r => r.data),

  getById: (wafId: number) =>
    api.get<ManagedWaf>(`/v1/waf/managed/${wafId}`).then(r => r.data),

  discover: () =>
    api.post<{ discovered: WafDiscoverCounts }>('/v1/waf/discover').then(r => r.data),
}

interface WafDiscoverCounts {
  tenants: number
  sessions_ok: number
  acls: number
  ipsets: number
}

const WORLD_LABEL: Record<ManagedWaf['world'], string> = {
  own:           'Próprio',
  institutional: 'Institucional',
  tenant:        'Tenant',
}

const WORLD_COLOR: Record<ManagedWaf['world'], string> = {
  own:           'blue',
  institutional: 'violet',
  tenant:        'teal',
}

export function WafRegistry() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const can = useCan()
  const canManage = can('waf', 'manage')
  const canProvision = can('waf', 'provision')
  const canDelete = can('waf', 'delete')
  // O painel de histórico é entre tenants, e a rota exige
  // `plataforma:global_view` além de `waf:view`. `global_read_all` é esse mesmo
  // bit, servido pelo /auth/me — sem isto, quem não pode ver a frota receberia
  // 403 e um painel vermelho.
  const user = useAuthStore((s) => s.user)
  const podeVerAFrota = !!(user?.global_read_all || user?.is_superuser)
  // O lote é multi-tenant: basta a permissão em algum tenant; o backend
  // confere WAF a WAF (spec 2026-10-18 D7).
  const podeAplicarEmLote = podeEmAlgumTenant(user, can, 'waf', 'rule:bulk')
  const [loteAberto, setLoteAberto] = useState(false)

  const [discoverError, setDiscoverError] = useState<string | null>(null)
  const [discoverSuccess, setDiscoverSuccess] = useState<string | null>(null)
  const [provisionOpen, setProvisionOpen] = useState(false)
  const [emAndamento, setEmAndamento] = useState<{
    tenantId: number; requestId: number; status: string; gmudId: number | null
    desiredName: string; albName: string | null; errorDetail?: string | null
    kind?: 'provision' | 'deletion'
  } | null>(null)
  const [excluindo, setExcluindo] = useState<
    { id: number; name: string; tenantId: number | null; recursos?: unknown[] } | null>(null)

  const wafs = useQuery({
    queryKey: ['waf', 'managed'],
    queryFn: () => wafApi.list(),
  })

  // Pedidos de provisionamento (09/08/2026). Desde o !1085 o provisionamento
  // acontece direto e em segundo plano quando a organizacao nao tem comite — sem
  // esta leitura, a falha e invisivel e o operador acredita que o ALB esta
  // protegido.
  const tenant = useCurrentTenant()
  const pedidos = useQuery({
    queryKey: ['waf', 'provisioning-requests', tenant?.id],
    queryFn: () => listProvisioningRequests(tenant!.id),
    enabled: !!tenant?.id,
    // So enquanto houver algo ANDANDO. Ligado sempre, isto bateria no endpoint a
    // cada 15s para todo operador com a pagina aberta, sem necessidade.
    refetchInterval: (q) => {
      const itens = (q.state.data as { items?: ProvisioningRequest[] } | undefined)?.items ?? []
      const andando = itens.some(
        (p) => p.status === 'pending' || p.status === 'pending_gmud',
      )
      return andando ? 15_000 : false
    },
  })
  const pedidosItens: ProvisioningRequest[] = pedidos.data?.items ?? []

  // "Tentar de novo": repete o POST com o corpo remontado a partir do proprio
  // pedido — a tela nao tem esses dados por outra via.
  //
  // NAO precisa cancelar o pedido antes por causa do dedupe: ele casa
  // IN ('pending','pending_gmud') e nao bloqueia `failed`. O cancelamento existe
  // para nao deixar dois cartoes contraditorios sobre o mesmo ALB — e e o
  // proprio backend que o faz, junto com a auditoria.
  // Descartar tira o cartao da lista e NAO apaga nada: o pedido continua no
  // historico, com o `status` terminal intacto. Ate 26/08/2026 um `failed`
  // ficava aqui para sempre, e foi um cartao de 10/08 — sem data e com botao de
  // repetir — que fez nascerem dois Web ACLs que ninguem pediu. (TASK-10169)
  const descartar = useMutation({
    mutationFn: (p: ProvisioningRequest) => dismissProvisioningRequest(p.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['waf', 'provisioning-requests'] })
    },
  })

  const retentar = useMutation({
    mutationFn: (p: ProvisioningRequest) => provisionWaf({
      tenant_id: tenant!.id,
      account_id: p.account_id,
      region: p.region,
      alb_arn: p.target_alb_arn,
      alb_name: p.target_alb_name ?? undefined,
      desired_name: p.desired_name,
      reason: p.reason ?? undefined,
      replace_request_id: p.id,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['waf', 'provisioning-requests'] })
    },
  })

  const discover = useMutation({
    mutationFn: () => wafApi.discover(),
    onSuccess: (data) => {
      const count = data.discovered?.acls ?? 0
      setDiscoverSuccess(`Descoberta concluída — ${count} WAF(s) encontrado(s).`)
      setDiscoverError(null)
      qc.invalidateQueries({ queryKey: ['waf', 'managed'] })
    },
    onError: (err: unknown) => {
      setDiscoverError(formatApiError(err, 'Erro ao descobrir WAFs.'))
      setDiscoverSuccess(null)
    },
  })

  const items = wafs.data?.items ?? []
  const isLoading = wafs.isLoading

  return (
    <Stack gap="xl">
      <Group justify="space-between" align="center">
        <Text size="lg" fw={700}>
          <IconShieldCheck size={20} style={{ verticalAlign: 'middle', marginRight: 6 }} />
          Registro de WAF
        </Text>

        <Group gap="sm">
          {podeAplicarEmLote && (
            <Button variant="light" onClick={() => setLoteAberto(true)}>
              Aplicar regra do catálogo
            </Button>
          )}
          {canProvision && (
            <Button
              leftSection={<IconPlus size={16} />}
              variant="light"
              onClick={() => setProvisionOpen(true)}
            >
              Provisionar WAF
            </Button>
          )}
          {canManage && (
            <Button
              leftSection={<IconRefresh size={16} />}
              color="teal"
              loading={discover.isPending}
              onClick={() => {
                setDiscoverError(null)
                setDiscoverSuccess(null)
                discover.mutate()
              }}
            >
              Descobrir WAFs
            </Button>
          )}
        </Group>
      </Group>

      {podeAplicarEmLote && <LotesRecentes />}
      {podeAplicarEmLote && <WafLoteModal opened={loteAberto} onClose={() => setLoteAberto(false)} />}

      <WafProvisionWizard
        opened={provisionOpen}
        onClose={() => {
          setProvisionOpen(false)
          qc.invalidateQueries({ queryKey: ['waf', 'managed'] })
        }}
        onProvisioned={(r) => setEmAndamento({
          // `r.tenant_id` vem do WIZARD, nao de `useCurrentTenant()`: os dois divergem
          // quando o operador provisiona para outro tenant, e ai o modal pedia um id
          // que a rota nao acha.
          tenantId: r.tenant_id, requestId: r.request_id, status: r.status,
          gmudId: r.gmud_id, desiredName: r.desired_name, albName: r.alb_name,
          // O pedido bloqueado nao e' consultado de novo (o poll do modal fica
          // desligado): o motivo so existe aqui, na resposta do POST.
          errorDetail: r.error_detail ?? null,
        })}
      />

      {/* Modal de progresso: mostra os marcos ENQUANTO o provisionamento roda, para quem
          clicou. O cartao abaixo continua servindo quem chega depois — outra sessao, outro
          operador, o dia seguinte. Ver a spec de 14/08, §5.7. */}
      {excluindo && (
        <WafDeleteModal
          opened
          onClose={() => setExcluindo(null)}
          wafId={excluindo.id}
          wafName={excluindo.name}
          tenantId={excluindo.tenantId}
          recursos={excluindo.recursos as never}
          onSolicitado={(r) => {
            // Emenda direto no modal de progresso: quem pediu a exclusao
            // acompanha os marcos, como no provisionamento.
            setEmAndamento({
              tenantId: excluindo.tenantId ?? 0,
              requestId: r.request_id, status: r.status, gmudId: r.gmud_id,
              desiredName: excluindo.name, albName: null, kind: 'deletion',
            })
          }}
        />
      )}

      {emAndamento && (
        <WafProvisioningModal
          tenantId={emAndamento.tenantId}
          requestId={emAndamento.requestId}
          statusInicial={emAndamento.status}
          errorDetailInicial={emAndamento.errorDetail}
          gmudId={emAndamento.gmudId}
          desiredName={emAndamento.desiredName}
          albName={emAndamento.albName}
          kind={emAndamento.kind}
          onClose={() => {
            setEmAndamento(null)
            qc.invalidateQueries({
              queryKey: ['waf', 'provisioning-requests', emAndamento.tenantId] })
            qc.invalidateQueries({ queryKey: ['waf', 'managed'] })
          }}
        />
      )}

      {/* Pedidos de provisionamento. Fica ACIMA da tabela de proposito: e a
          resposta a "cade o WAF que eu pedi?", e essa pergunta vem antes de
          olhar a lista. */}
      {pedidosItens.map((p) => {
        const alvo = p.target_alb_name || p.target_alb_arn
        // `kind` chega da rota desde 17/08. Ausente = pedido gravado antes da
        // MIGRATION_226, e aqueles são todos de provisionamento.
        const ehExclusao = p.kind === 'deletion'
        if (p.status === 'blocked') {
          return (
            <Alert key={p.id} color="orange" icon={<IconAlertTriangle size={16} />}
                   title={`Sem permissão para provisionar WAF em ${alvo}`}>
              {/* Titulo nomeia o ALVO; corpo nomeia o Web ACL pedido. Os dois sao
                  fatos diferentes e podem divergir — em 26/08 divergiram, e o
                  titulo (que saia de `desired_name`) nomeou um ALB que nunca foi
                  tocado. TASK-10170. */}
              <Text size="sm">
                Web ACL <b>{p.desired_name}</b> — <b>{p.error_detail || 'a conta não permite criar Web ACL'}</b>
              </Text>
              <ProvisioningTimeline steps={p.steps} />
              <QuandoPedido iso={p.created_at} />
              {/* Sem botão "Tentar de novo" de propósito: repetir sem mexer no IAM
                  da conta dá exatamente o mesmo resultado, e o botão ensinaria o
                  operador a insistir no que não depende dele. */}
              <Text size="xs" mt={4}>
                Quem administra a conta AWS precisa conceder as permissões acima à
                role do ConsoleAI. Depois disso, peça o provisionamento novamente.
              </Text>
              <Button size="xs" mt="xs" ml="xs" variant="subtle" color="gray"
                      loading={descartar.isPending}
                      onClick={() => descartar.mutate(p)}>
                Descartar
              </Button>
            </Alert>
          )
        }
        if (p.status === 'failed') {
          return (
            <Alert key={p.id} color="red" icon={<IconAlertTriangle size={16} />}
                   title={ehExclusao
                     ? `Falha ao excluir ${p.desired_name}`
                     : `Falha ao provisionar WAF em ${alvo}`}>
              <Text size="sm">
                Web ACL <b>{p.desired_name}</b> — <b>{p.error_detail || 'motivo não registrado'}</b>
              </Text>
              <ProvisioningTimeline steps={p.steps} />
              <QuandoPedido iso={p.created_at} />
              {/* A resposta que ninguém conseguiu dar olhando a tela em 10/08:
                  o Web ACL existe, e tentar de novo ADOTA o que já foi criado. */}
              {p.created_web_acl_arn && (
                <Text size="xs" mt={4}>
                  O Web ACL <b>{p.desired_name}</b> já existe nesta conta. Tentar
                  de novo <b>adota</b> o que já foi criado — não cria um segundo.
                </Text>
              )}
              {/* Nunca numa exclusão: `retentar` chama `provisionWaf`, e ali o botão
                  CRIARIA o Web ACL que se pediu para apagar — sem passar por
                  `waf:delete`. Medido em 17/08. */}
              {canProvision && !ehExclusao && (
                <Button size="xs" mt="xs" variant="light" color="red"
                        loading={retentar.isPending}
                        onClick={() => retentar.mutate(p)}>
                  Tentar de novo
                </Button>
              )}
              <Button size="xs" mt="xs" ml="xs" variant="subtle" color="gray"
                      loading={descartar.isPending}
                      onClick={() => descartar.mutate(p)}>
                Descartar
              </Button>
            </Alert>
          )
        }
        if (p.status === 'cancelled') {
          return (
            <Alert key={p.id} color="gray"
                   title={ehExclusao
                     ? `Exclusão cancelada — ${p.desired_name}`
                     : `Provisionamento cancelado — ${alvo}`}>
              <Text size="sm">
                Web ACL <b>{p.desired_name}</b> — {p.error_detail || 'sem motivo registrado'}
              </Text>
              <ProvisioningTimeline steps={p.steps} />
              <QuandoPedido iso={p.created_at} />
              <Button size="xs" mt="xs" ml="xs" variant="subtle" color="gray"
                      loading={descartar.isPending}
                      onClick={() => descartar.mutate(p)}>
                Descartar
              </Button>
            </Alert>
          )
        }
        if (p.status === 'pending_gmud') {
          return (
            <Alert key={p.id} color="blue" title={`Aguardando aprovação — ${p.desired_name}`}>
              <Text size="sm">
                {alvo}
                {/* O número da GMUD vira LINK. Era texto puro, e este cartão
                    ficava sendo o único da lista sem saída nenhuma: quem
                    pediu o WAF e mudou de ideia não tinha de onde desistir —
                    nem sabia que existe uma tela de GMUD. Cancelar mora lá, e
                    não aqui, porque quem cancela é a GMUD; quando ela fecha,
                    este pedido volta como `cancelled` no refetch. */}
                {p.gmud_id ? (
                  <> — <Anchor component={Link} to={`/gmud/${p.gmud_id}`} inherit>
                    GMUD #{p.gmud_id}
                  </Anchor></>
                ) : null}
                . O WAF aparece no registro após a aprovação.
              </Text>
              {p.gmud_id ? (
                <Text size="xs" c="dimmed" mt={4}>
                  Para desistir do pedido, cancele a GMUD.
                </Text>
              ) : null}
              <ProvisioningTimeline steps={p.steps} />
              <QuandoPedido iso={p.created_at} />
            </Alert>
          )
        }
        return (
          <Alert key={p.id} color="teal" icon={<Loader size={14} />}
                 title={ehExclusao
                   ? `Excluindo ${p.desired_name}…`
                   : `Provisionando ${p.desired_name}…`}>
            <Text size="sm">
              {alvo}
              {/* Se ha error_detail com status `pending`, o scheduler ja tentou e
                  falhou — dizer so "Provisionando..." seria mentir. */}
              {p.error_detail
                ? ` — tentando desde ${new Date(p.created_at).toLocaleString()}. Última falha: ${p.error_detail}`
                : ''}
            </Text>
            <ProvisioningTimeline steps={p.steps} />
          </Alert>
        )
      })}

      {discoverError && (
        <Alert color="red" onClose={() => setDiscoverError(null)} withCloseButton>
          {discoverError}
        </Alert>
      )}

      {discoverSuccess && (
        <Alert color="green" onClose={() => setDiscoverSuccess(null)} withCloseButton>
          {discoverSuccess}
        </Alert>
      )}

      {wafs.isError && (
        <Alert color="red">
          {formatApiError(wafs.error, 'Erro ao carregar lista de WAFs.')}
        </Alert>
      )}

      {isLoading ? (
        <Stack gap="xs">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} height={40} />
          ))}
        </Stack>
      ) : items.length === 0 ? (
        <Text size="sm" c="dimmed" ta="center" py="xl">
          {/* Com pedido pendente ou falho, mandar "Descobrir WAFs" nao resolve
              nada — o WAF nao existe ainda, ou nao chegou a ser criado. */}
          {pedidosItens.length > 0
            ? 'Nenhum WAF registrado ainda — veja o estado do seu pedido acima.'
            : 'Nenhum WAF registrado. Clique em "Descobrir WAFs" para iniciar a varredura.'}
        </Text>
      ) : (
        <Table striped highlightOnHover withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Nome</Table.Th>
              <Table.Th>Conta</Table.Th>
              <Table.Th>Região</Table.Th>
              <Table.Th>Escopo</Table.Th>
              <Table.Th>Mundo</Table.Th>
              <Table.Th>Protege</Table.Th>
              <Table.Th>Ação padrão</Table.Th>
              <Table.Th>Gerenciada</Table.Th>
              <Table.Th>Sensível</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {items.map((waf) => (
              <Table.Tr
                key={waf.id}
                onClick={() => navigate(`/security/waf/${waf.id}`)}
                style={{ cursor: 'pointer' }}
              >
                <Table.Td>
                  <Text size="sm" fw={500}>{waf.name}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="xs" ff="monospace" c="dimmed">{waf.account_id}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{waf.region}</Text>
                </Table.Td>
                <Table.Td>
                  <Badge size="sm" variant="light" color={waf.scope === 'CLOUDFRONT' ? 'orange' : 'cyan'}>
                    {waf.scope}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Badge size="sm" variant="light" color={WORLD_COLOR[waf.world]}>
                    {WORLD_LABEL[waf.world]}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Stack gap={2}>
                    <Text size="sm">
                      {(waf.resource_count ?? 0)} {(waf.resource_count ?? 0) === 1 ? 'recurso' : 'recursos'}
                    </Text>
                    <Text size="xs" c="dimmed" ff="monospace">
                      {waf.primary_domain || '—'}
                    </Text>
                  </Stack>
                </Table.Td>
                <Table.Td>
                  <Badge
                    size="sm"
                    variant="light"
                    color={waf.default_action?.toUpperCase() === 'ALLOW' ? 'green' : 'red'}
                  >
                    {waf.default_action ?? '—'}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Badge size="sm" variant="dot" color={waf.is_managed ? 'teal' : 'gray'}>
                    {waf.is_managed ? 'Sim' : 'Não'}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Badge size="sm" variant="dot" color={waf.sensitive ? 'red' : 'gray'}>
                    {waf.sensitive ? 'Sim' : 'Não'}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  {canDelete && waf.is_managed ? (
                    <Button
                      size="compact-xs" variant="subtle" color="red"
                      onClick={(e) => {
                        // `stopPropagation` OBRIGATORIO: a <Table.Tr> tem onClick que
                        // navega para o detalhe do WAF. Sem isto o clique borbulha, a
                        // pagina troca, e o modal de confirmacao NUNCA aparece —
                        // medido no aceite de 16/08, com o botao ja em producao.
                        e.stopPropagation()
                        setExcluindo({
                          id: waf.id, name: waf.name,
                          tenantId: waf.tenant_id ?? null,
                          recursos: (waf as { resources?: unknown[] }).resources,
                        })
                      }}
                    >
                      Excluir
                    </Button>
                  ) : null}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}

      {/* Histórico DEPOIS do registro: quem chega procura primeiro o que existe,
          e só então o que aconteceu. */}
      {podeVerAFrota && <WafProvisioningHistory />}
    </Stack>
  )
}
